import { useEffect, useRef, useState } from 'react';
import { resolveLocationTimezone } from '../../domain/locationTimezone.js';
import type { OperatingSchedule } from '../../domain/types.js';
import { fetchLocationTimezone, shouldResolveLocationTimezone } from './locationSpelling.js';

const LOCATION_DEBOUNCE_MS = 600;

export type LocationTimezoneStatus =
  | { phase: 'idle' }
  | { phase: 'pending' }
  | { phase: 'done'; timezone?: string; correctedRegion?: string };

/**
 * When timezone strategy is location, ask DeepSeek for corrected location + timezone.
 * Map wins for known places; validated DeepSeek timezone fills gaps. Ignores stale responses.
 */
export function useLocationTimezoneResolution(args: {
  locationRegion: string | undefined;
  schedule: OperatingSchedule | undefined;
  onLocationCorrected: (region: string) => void;
  onScheduleUpdated: (schedule: OperatingSchedule) => void;
}): LocationTimezoneStatus {
  const [status, setStatus] = useState<LocationTimezoneStatus>({ phase: 'idle' });
  const requestToken = useRef(0);
  const lastFetchedInput = useRef<string | null>(null);
  const onCorrectRef = useRef(args.onLocationCorrected);
  const onScheduleRef = useRef(args.onScheduleUpdated);
  const scheduleRef = useRef(args.schedule);
  const locationRegionRef = useRef(args.locationRegion);
  onCorrectRef.current = args.onLocationCorrected;
  onScheduleRef.current = args.onScheduleUpdated;
  scheduleRef.current = args.schedule;
  locationRegionRef.current = args.locationRegion;

  useEffect(() => {
    const trimmed = args.locationRegion?.trim() ?? '';
    const strategy = args.schedule?.timezoneStrategy ?? 'location';

    if (!trimmed || strategy !== 'location' || !shouldResolveLocationTimezone(trimmed)) {
      setStatus({ phase: 'idle' });
      return;
    }

    if (lastFetchedInput.current === trimmed) {
      return;
    }

    let cancelled = false;
    const debouncedInput = trimmed;

    const timer = window.setTimeout(() => {
      if (cancelled) return;

      const currentTrimmed = locationRegionRef.current?.trim() ?? '';
      if (currentTrimmed !== debouncedInput) return;
      if (lastFetchedInput.current === currentTrimmed) return;

      const sourceInput = currentTrimmed;
      lastFetchedInput.current = sourceInput;
      const token = ++requestToken.current;

      void (async () => {
        setStatus({ phase: 'pending' });
        const result = await fetchLocationTimezone(sourceInput);
        if (cancelled || token !== requestToken.current) return;

        const stillTrimmed = locationRegionRef.current?.trim() ?? '';
        if (stillTrimmed !== sourceInput) return;

        const locationForMap = result?.locationForMap ?? sourceInput;
        const timezone =
          result?.timezone ?? resolveLocationTimezone(locationForMap) ?? undefined;

        if (result?.correctedLocation) {
          lastFetchedInput.current = result.correctedLocation;
          onCorrectRef.current(result.correctedLocation);
        }

        const currentSchedule = scheduleRef.current;
        if (currentSchedule?.timezoneStrategy === 'location') {
          const nextSchedule: OperatingSchedule = { ...currentSchedule, timezone };
          if (nextSchedule.timezone !== currentSchedule.timezone) {
            onScheduleRef.current(nextSchedule);
          }
        }

        setStatus({
          phase: 'done',
          timezone,
          correctedRegion: result?.correctedLocation,
        });
      })();
    }, LOCATION_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [args.locationRegion, args.schedule?.timezoneStrategy]);

  return status;
}
